import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import useSWR from 'swr'
import {
  IEvaluationResume,
  IEvaluationSection,
  IQuestion,
  IQuestionLikert,
  IQuestionMultipleChoice,
  IQuestionOpen,
} from 'models/evaluation.model'
import { EvaluationResponseStatus } from 'enum/evaluationResponseStatus.enum'
import { evaluationsService } from 'api/courses/evaluations'
import { questionBankService } from 'api/administration/questionBank'
import { QuestionTypes } from 'enum'
import { useCourse } from 'contexts'
import EvaluationUpdated from '../EvaluationUpdated'

interface IEvaluationContext {
  evaluation: IEvaluationResume
  isReviewing: boolean
  isSectionsInReviewMode: boolean
  sections: {
    data: IEvaluationSection[] | null
    isLoading: boolean
  }
  updateSections: (
    questionId: number,
    userAnswer: IQuestion['userAnswer']
  ) => void
  savingQuestions: Record<number, boolean>
  setSavingQuestion: (questionId: number, saving: boolean) => void
  isAnySaving: boolean
  setOnEvaluation: (value: boolean) => void
  refreshSections: (forceReview?: boolean) => void
  refreshEvaluation: () => Promise<void>
  checkForEvaluationUpdates: () => Promise<boolean>
  onEvaluationUpdated: (newEvaluation: IEvaluationResume) => void
  previewMode: boolean
}

export const EvaluationContext = createContext<IEvaluationContext>(
  Object.create(null)
)

export const EvaluationProvider: React.FC<{
  evaluation: IEvaluationResume
  setOnEvaluation: (value: boolean) => void
  onEvaluationUpdated: (newEvaluation: IEvaluationResume) => void
}> = ({
  evaluation: initialEvaluation,
  children,
  setOnEvaluation,
  onEvaluationUpdated,
}) => {
  const [evaluation, setEvaluation] = useState(initialEvaluation)
  const { previewMode } = useCourse()
  const isReviewing =
    evaluation.status === EvaluationResponseStatus.completed ||
    evaluation.status === EvaluationResponseStatus.abandoned

  const [isLoadingSections, setIsLoadingSections] = useState(false)
  const [sections, setSections] = useState<IEvaluationSection[]>([])
  const [isSectionsInReviewMode, setIsSectionsInReviewMode] = useState(false)
  const [savingQuestions, setSavingQuestions] = useState<
    Record<number, boolean>
  >({})
  const [showEvaluationUpdated, setShowEvaluationUpdated] = useState(false)

  const { data: currentVersion, isLoading: isLoadingCurrentVersion } = useSWR(
    `evaluation-number-${evaluation.id}-${evaluation.versionId}`,
    async () => await evaluationsService.getEvaluation(evaluation.id),
    {
      refreshInterval: 60000 * 2, // 2 minutes
    }
  )

  const isSameVersion = useCallback(
    (versionId: number) => versionId === evaluation.versionId,
    [evaluation.versionId]
  )

  useEffect(() => {
    if (isLoadingCurrentVersion || !currentVersion) return

    const inProgress =
      currentVersion.status === EvaluationResponseStatus.inProgress
    setShowEvaluationUpdated(
      inProgress && !isSameVersion(currentVersion.versionId)
    )
  }, [isSameVersion, isLoadingCurrentVersion, currentVersion])

  const refreshEvaluation = useCallback(async () => {
    try {
      const updatedEvaluation = await evaluationsService.getEvaluation(
        evaluation.id
      )
      setEvaluation(updatedEvaluation)
    } catch (error) {
      // Handle error silently
    }
  }, [evaluation.id])

  const refreshSections = useCallback(
    async (forceReview?: boolean) => {
      setIsLoadingSections(true)
      try {
        if (previewMode) {
          const sections = await questionBankService.getSections(evaluation.id)
          return setSections(sections)
        }

        // The review mode is used to get the sections in review mode (when the user finished the evaluation)
        const reviewMode =
          (forceReview !== undefined ? forceReview : isReviewing) &&
          evaluation.showResults
        setIsSectionsInReviewMode(reviewMode)
        const sections = await evaluationsService.getEvaluationSections(
          evaluation.id,
          { review: reviewMode }
        )
        setSections(sections)
      } finally {
        setIsLoadingSections(false)
      }
    },
    [evaluation.id, isReviewing, previewMode, evaluation.showResults]
  )

  // Efecto inicial para obtener el estado actualizado de la evaluación
  useEffect(() => {
    const initializeEvaluation = async () => {
      try {
        setEvaluation(
          await evaluationsService.getEvaluation(initialEvaluation.id)
        )
      } catch (error) {
        // Handle error silently
      }
    }

    initializeEvaluation()
  }, [initialEvaluation])

  useEffect(() => {
    refreshSections()
  }, [refreshSections])

  // Sincronizar isSectionsInReviewMode cuando cambie isReviewing
  useEffect(() => {
    setIsSectionsInReviewMode(isReviewing)
  }, [isReviewing])

  const setSavingQuestion = useCallback(
    (questionId: number, saving: boolean) => {
      setSavingQuestions((prev) => ({
        ...prev,
        [questionId]: saving,
      }))
    },
    []
  )

  const isAnySaving = Object.values(savingQuestions).some(Boolean)

  const updateSections = useCallback(
    (questionId: number, userAnswer: IQuestion['userAnswer']) => {
      setSections((prev) =>
        prev.map((section) => {
          return {
            ...section,
            questions: section.questions.map((question): IQuestion => {
              if (question.id !== questionId) return question

              const { open, likert, multipleChoice } = QuestionTypes

              if (!userAnswer?.response)
                return {
                  ...question,
                  userAnswer: null,
                }

              const { response } = userAnswer

              if (question.type === open && typeof response === 'string')
                return {
                  ...question,
                  userAnswer: { response },
                } as IQuestionOpen

              if (question.type === likert && typeof response === 'number')
                return {
                  ...question,
                  userAnswer: { response },
                } as IQuestionLikert

              if (question.type === multipleChoice && Array.isArray(response))
                return {
                  ...question,
                  userAnswer: { response },
                } as IQuestionMultipleChoice

              return question
            }),
          }
        })
      )
    },
    []
  )

  const checkForEvaluationUpdates = useCallback(async () => {
    try {
      const updatedEvaluation = await evaluationsService.getEvaluation(
        evaluation.id
      )
      const isCurrentVersion = isSameVersion(updatedEvaluation.versionId)
      const hasUpdates = !isCurrentVersion

      setShowEvaluationUpdated(hasUpdates)

      return isCurrentVersion
    } catch (error) {
      console.error('Error checking for evaluation updates:', error)
      // Return true to allow operation to continue if check fails
      return true
    }
  }, [evaluation.id, isSameVersion])

  return (
    <EvaluationContext.Provider
      value={{
        evaluation,
        isReviewing,
        isSectionsInReviewMode,
        sections: {
          data: sections,
          isLoading: isLoadingSections,
        },
        updateSections,
        savingQuestions,
        setSavingQuestion,
        isAnySaving,
        setOnEvaluation,
        refreshSections,
        refreshEvaluation,
        checkForEvaluationUpdates,
        onEvaluationUpdated,
        previewMode,
      }}
    >
      <EvaluationUpdated
        show={showEvaluationUpdated}
        onReload={async () => {
          await evaluationsService.createEvaluationResponse(evaluation.id)
          const updatedEvaluation = await evaluationsService.getEvaluation(
            evaluation.id
          )
          setShowEvaluationUpdated(false)
          setEvaluation(updatedEvaluation)
          onEvaluationUpdated(updatedEvaluation)
          await refreshSections()
        }}
        onClose={async () => {
          onEvaluationUpdated(
            await evaluationsService.getEvaluation(evaluation.id)
          )
          setOnEvaluation(false)
        }}
      />
      {children}
    </EvaluationContext.Provider>
  )
}

export const useEvaluation = () => useContext(EvaluationContext)
